/**
 * StyleBay AdMob wrapper.
 *
 * Native (Android/iOS via Capacitor): calls the real @capacitor-community/admob
 *   plugin with either the env-configured production Ad Unit IDs or, as a
 *   sensible default, Google's official test IDs (which are safe to ship
 *   during development — they never trigger policy violations).
 *
 * Web (dev + preview): every function no-ops. The React app never crashes
 *   from a missing native plugin.
 *
 * Rewarded ads: `showRewardedAd()` resolves to `true` iff the user actually
 *   earned the reward (i.e. watched the full video). The existing
 *   `openAdModal()` fallback is used on web so free-tier gating still works
 *   during PWA testing.
 */

// Google's official test ad unit ids (Universal — https://developers.google.com/admob/android/test-ads)
const TEST_IDS = {
  appId:        'ca-app-pub-3940256099942544~3347511713',
  appOpen:      'ca-app-pub-3940256099942544/9257395921',
  banner:       'ca-app-pub-3940256099942544/6300978111',
  interstitial: 'ca-app-pub-3940256099942544/1033173712',
  rewarded:     'ca-app-pub-3940256099942544/5224354917',
};

// Production IDs come from environment variables so CI can bake real IDs in
// without touching this file. Missing values fall back to the safe test IDs.
export const AD_IDS = {
  appOpen:      process.env.REACT_APP_ADMOB_APP_OPEN      || TEST_IDS.appOpen,
  banner:       process.env.REACT_APP_ADMOB_BANNER        || TEST_IDS.banner,
  interstitial: process.env.REACT_APP_ADMOB_INTERSTITIAL  || TEST_IDS.interstitial,
  rewarded:     process.env.REACT_APP_ADMOB_REWARDED      || TEST_IDS.rewarded,
};

// If any of the AD_IDS above is missing our REACT_APP_ADMOB_* env var, that
// AD unit will still be a Google test id — we enable `isTesting: true` on
// the AdMob plugin in that case so we never accidentally consume real ad
// inventory during preview builds.
const USING_TEST_IDS = Object.values(AD_IDS).some(
  (v) => v.startsWith('ca-app-pub-3940256099942544')
);

let _initialized = false;
let _bannerVisible = false;

function isNative() {
  // Duck-typed to avoid pulling @capacitor/core here — this file is imported
  // from all sorts of components and we want tree-shaking friendly access.
  return !!(typeof window !== 'undefined' &&
            window.Capacitor &&
            (window.Capacitor.isNativePlatform
              ? window.Capacitor.isNativePlatform()
              : window.Capacitor.isNative));
}

async function _plugin() {
  const mod = await import('@capacitor-community/admob');
  return mod;
}

/**
 * Initialise the AdMob SDK. Safe to call multiple times.
 * Should be invoked exactly once at app boot from AuthProvider or App.js.
 */
export async function initAdMob() {
  if (!isNative() || _initialized) return;
  try {
    const { AdMob } = await _plugin();
    await AdMob.initialize({
      // Test device IDs can be added here once real production IDs are wired.
      testingDevices: [],
      initializeForTesting: USING_TEST_IDS,
    });
    _initialized = true;
    // eslint-disable-next-line no-console
    console.info('[AdMob] initialised', USING_TEST_IDS ? '(TEST MODE)' : '(PROD)');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[AdMob] initialize failed:', e?.message || e);
  }
}

/**
 * App Open Ads — shown when the user resumes the app from background.
 * Silently no-ops if the SDK isn't ready or an ad isn't available.
 */
export async function showAppOpenAd() {
  if (!isNative()) return false;
  try {
    const { AdMob } = await _plugin();
    await AdMob.prepareAppOpenAd({ adId: AD_IDS.appOpen, isTesting: USING_TEST_IDS });
    await AdMob.showAppOpenAd();
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[AdMob] app-open failed:', e?.message || e);
    return false;
  }
}

/**
 * Adaptive bottom-banner ad. Auto-adds a margin equal to the app's fixed
 * bottom-nav height so we don't cover the nav. Idempotent — calling twice
 * just leaves the existing banner in place.
 */
export async function showBanner() {
  if (!isNative() || _bannerVisible) return false;
  try {
    const { AdMob, BannerAdSize, BannerAdPosition } = await _plugin();
    await AdMob.showBanner({
      adId: AD_IDS.banner,
      adSize: BannerAdSize.ADAPTIVE_BANNER,
      position: BannerAdPosition.BOTTOM_CENTER,
      // Push banner up above our fixed bottom nav (~72px) so tabs stay tappable.
      margin: 72,
      isTesting: USING_TEST_IDS,
    });
    _bannerVisible = true;
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[AdMob] banner failed:', e?.message || e);
    return false;
  }
}

export async function hideBanner() {
  if (!isNative() || !_bannerVisible) return;
  try {
    const { AdMob } = await _plugin();
    await AdMob.hideBanner();
    _bannerVisible = false;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[AdMob] hideBanner failed:', e?.message || e);
  }
}

/**
 * Interstitial Ad — full-screen ad. Prepare + show. Silently no-ops on
 * failure so it never breaks user flow.
 */
export async function showInterstitial() {
  if (!isNative()) return false;
  try {
    const { AdMob } = await _plugin();
    await AdMob.prepareInterstitial({ adId: AD_IDS.interstitial, isTesting: USING_TEST_IDS });
    await AdMob.showInterstitial();
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[AdMob] interstitial failed:', e?.message || e);
    return false;
  }
}

/**
 * Rewarded Ad — resolves(true) only if the user actually earned the reward
 * (watched the full video). On web / plugin failure we resolve(false) so
 * the caller can fall back to the simulated AdModal.
 *
 * `customData` is passed through to the SSV callback (future work).
 */
export async function showRewardedAd({ customData } = {}) {
  if (!isNative()) return false;
  try {
    const { AdMob, RewardAdPluginEvents } = await _plugin();

    // Race between "user earned reward" and "ad dismissed without reward".
    const reward = await new Promise((resolve) => {
      let handled = false;
      const cleanup = () => {
        try { rewardSub?.remove(); } catch { /* noop */ }
        try { dismissSub?.remove(); } catch { /* noop */ }
      };
      const finish = (val) => { if (!handled) { handled = true; cleanup(); resolve(val); } };

      // eslint-disable-next-line prefer-const
      let rewardSub, dismissSub;
      AdMob.addListener(RewardAdPluginEvents.Rewarded, () => finish(true)).then((s) => { rewardSub = s; });
      AdMob.addListener(RewardAdPluginEvents.Dismissed, () => finish(false)).then((s) => { dismissSub = s; });

      AdMob.prepareRewardVideoAd({
        adId: AD_IDS.rewarded,
        isTesting: USING_TEST_IDS,
        ssv: customData ? { customData } : undefined,
      })
        .then(() => AdMob.showRewardVideoAd())
        .catch(() => finish(false));
    });

    return reward;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[AdMob] rewarded failed:', e?.message || e);
    return false;
  }
}

export function isAdMobAvailable() {
  return isNative();
}
